/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../app/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI'
], (StringResources, APIFactory, PropertiesProviderAPI) => {
	const TAB_POSITION = [
		{
			'value': 'top',
			'label': StringResources.get('propTop')
		},
		{
			'value': 'left',
			'label': StringResources.get('propLeft')
		},
		{
			'value': 'bottom',
			'label': StringResources.get('propBottom')
		},
		{
			'value': 'right',
			'label': StringResources.get('propRight')
		}
	];

	const TAB_ICON_POSITION = [
		{
			'value': 'right',
			'label': StringResources.get('propRightTitle')
		},
		{
			'value': 'left',
			'label': StringResources.get('propLeftTitle')
		},
		{
			'value': 'top',
			'label': StringResources.get('propTopTitle')
		},
		{
			'value': 'bottom',
			'label': StringResources.get('propBottomTitle')
		}
	];

	class TabProperties {
		constructor({ dashboardApi, dashboardAPI, features, content, options }) {
			this._dashboard = dashboardApi || dashboardAPI;
			const featureChecker = this._dashboard && this._dashboard.getGlassCoreSvc('.FeatureChecker');
			this.isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');
			this._content = content;
			this._features = features;
			this._allowHidingMultipleTabs = !options || options.allowHidingMultipleTabs !== false;
			this._canvas = features['Canvas'];

			this._content.getFeature('Properties').registerProvider(this.getAPI());
		}

		_getCanvas() {
			return this._canvas;
		}

		getLayoutModel(id = this._content.getId()) {
			const boardModel = this._dashboard.getFeature('internal').getBoardModel();
			return boardModel.layout.findModel(id);
		}

		getAPI() {
			return APIFactory.createAPI(this, [PropertiesProviderAPI]);
		}

		getPropertyList() {
			const propertyList = this._getTabPropertiesList();

			// Default the setPropertyValue and getPropertyValue for any property that doesn't define those methods
			propertyList.forEach((property) => {
				if (!property.setPropertyValue) {
					property.setPropertyValue = (value) => this._setPropertyValueInModel(property.id, value);
				}

				if (!property.getPropertyValue) {
					property.getPropertyValue = () => this._getPropertyValueFromModel(property.id);
				}
			});

			return propertyList;
		}

		getPropertyLayoutList() {
			const propertiesLayoutSpec = [{
				id: 'general',
				type: 'Group',
				label: StringResources.get('tabName_general')
			}, {
				id: 'tabs',
				type: 'Section',
				label: StringResources.get('sectionName_tabs'),
				position: 3
			}, {
				id: 'masterTabStyles',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propMasterTabStylesLabel')
			}, {
				id: 'individualTabStyles',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propIndividualTabStylesLabel')
			}];
			if (this.isPropertiesV2Enabled) {
				// remove group as Carbon design no longer have Tab
				propertiesLayoutSpec.splice(0, 1);
			}
			return propertiesLayoutSpec;
		}

		reset() { }

		_individualTabSlideOut(page) {
			// Waiting for the content is Ready in here will force the property provider content feature to be registered
			// on page that are not rendered yet (since we delay the features)
			// TODO - This a temporary hack for now to get it to work in 11.2,  but we need to clean this up and have a better story
			this._dashboard.getFeature('Canvas.internal').whenContentReady(page.getId()).then(() => {
				const propertiesManager = this._dashboard.getFeature('propertiesManager');
				return propertiesManager.getPropertyLayoutList([page]).then(properties => {
					return propertiesManager.addChild({
						overlay: true,
						label: StringResources.get('individualTabProperties'),
						content: {
							module: 'dashboard/contentpane/PropertyUIControlView',
							items: properties,
							content: page,
							refreshChild: true
						},
						pageChildId: page.getId()
					});
				});
			});
		}

		_getIndividualTabStylesLinks(sectionIndividualTabStylesId) {
			const pages = this._getCanvas().findContent({ type: 'page' });
			return pages.map((page, index) => {
				const clickCallback = () => {
					this._individualTabSlideOut(page);
				};
				const layoutModel = this.getLayoutModel(page.getId());
				let tabIcon = page.getPropertyValue('tabIcon');

				if (!tabIcon){
					// This case is hack for now. We are not loading the features for non visible tabs which cause the property 
					// provider to to register the proper getPropertyValue.
					// TODO - The real fix is to move the property to proper place and stop using a cutom getPropertyValue
					const icon = layoutModel.get('tabIcon');
					if(icon && icon !== 'transparent') {
						tabIcon = this._features['Dashboard.Icons'].getIcon(icon).id;
					} else {
						tabIcon = 'transparent';
					}
				}


				// Defaulting to the layoutModel is hack for now. We are not loading the features for non visible tabs which cause the property 
				// provider to to register the proper getPropertyValue.
				// TODO - The real fix is to move the property to proper place and stop using a cutom getPropertyValue
				const title = page.getPropertyValue('title') ||  layoutModel.get('title');

				return {
					id: 'tabIndividualStylingLink' + index,
					editor: {
						sectionId: sectionIndividualTabStylesId,
						uiControl: {
							type: 'SingleLineLinks',
							clickCallback: clickCallback,
							items: [{
								align: 'left',
								items: [{
									type: 'icon',
									svgIcon: tabIcon !== 'transparent' ? tabIcon : null,
									iconTooltip: StringResources.get('propIndividualTabStylesAriaLabel')
								}]
							}, {
								align: 'right',
								items: [{
									type: 'text',
									value: title,
									showRightArrow: true
								}]
							}]
						}
					}
				};
			});
		}

		_getTabPropertiesList() {
			let sectionId, sectionIndividualTabStylesId;
			if (this.isPropertiesV2Enabled) {
				sectionId = 'tabs';
				sectionIndividualTabStylesId = 'tabs.individualTabStyles';
			} else {
				sectionId = 'general.tabs';
				sectionIndividualTabStylesId = 'general.tabs.individualTabStyles';
			}
			const sectionMasterTabStyles = sectionId + '.masterTabStyles';
			const restoreMasterStyling = () => {
				const transaction = this._dashboard.getFeature('Transaction');
				const transactionToken = transaction.startTransaction();
				this._getCanvas().setPropertyValue('tabTextColor', 'transparent', transactionToken);
				this._getCanvas().setPropertyValue('tabSelectedLineColor', 'transparent', transactionToken);
				this._getCanvas().setPropertyValue('tabBackgroundColor', 'transparent', transactionToken);
				transaction.endTransaction(transactionToken);
				this._dashboard.triggerDashboardEvent('properties:refreshPane', {
					focusSelector: '.clickCallback_left_0_tabRestoreDefaults'
				});
			};

			const model = this.getLayoutModel();
			const disableShowTabs = !this._allowHidingMultipleTabs && model.items.length > 1;
			const propertyList = [{
				id: 'showTabsInPreview',
				supportsUndoRedo: true,
				getPropertyValue: () => !model.get('hideTab'),
				setPropertyValue: (value) => {
					const oldValue = !model.get('hideTab');
					if (!oldValue || oldValue !== value) {
						model.set({
							'hideTab': !value
						}, {
							payloadData: { skipUndoRedo: true }
						});
					}
				},
				editor: {
					sectionId: sectionId,
					uiControl: {
						type: 'ToggleButton',
						label: StringResources.get('propShowTabsInPreview'),
						readOnly: disableShowTabs,
						disabled: disableShowTabs
					}
				}
			}, {
				id: 'tabPosition',
				supportsUndoRedo: true,
				getPropertyValue: () => model.get('tabPosition'),
				setPropertyValue: (value) => {
					const oldValue = model.get('tabPosition');
					if (!oldValue || oldValue !== value) {
						model.set({
							'tabPosition': value
						}, {
							payloadData: { skipUndoRedo: true }
						});
					}
				},
				editor: {
					sectionId: sectionId,
					uiControl: {
						type: 'DropDown',
						label: StringResources.get('propTabPosition'),
						options: TAB_POSITION
					}
				}
			}, {
				id: 'tabIconPosition',
				supportsUndoRedo: true,
				getPropertyValue: () => model.get('tabIconPosition'),
				setPropertyValue: (value) => {
					const oldValue = model.get('tabIconPosition');
					if (!oldValue || oldValue !== value) {
						model.set({
							'tabIconPosition': value
						}, {
							payloadData: { skipUndoRedo: true }
						});
					}
				},
				editor: {
					sectionId: sectionId,
					uiControl: {
						type: 'DropDown',
						label: StringResources.get('propTabIconPosition'),
						options: TAB_ICON_POSITION
					}
				}
			}, {
				id: 'tabTextColor',
				supportsUndoRedo: true,
				setPropertyValue: (value, transactionToken) => {
					this._changeTabColor('tabTextColor', value, transactionToken);
				},
				editor: {
					sectionId: sectionMasterTabStyles,
					uiControl: {
						type: 'ColorPicker',
						label: StringResources.get('propTabTextColor'),
						ariaLabel: StringResources.get('propTabTextColor'),
						paletteType: 'DashboardColorSet',
						open: false,
						showHexValue: true,
						addButton: true,
						onChange: (propertyName, propertyValueInfo = {}) => {
							this._getCanvas().setPropertyValue(propertyName, propertyValueInfo.name);
						}
					}
				}
			}, {
				id: 'tabSelectedLineColor',
				supportsUndoRedo: true,
				setPropertyValue: (value, transactionToken) => {
					this._changeTabColor('tabSelectedLineColor', value, transactionToken);
				},
				editor: {
					sectionId: sectionMasterTabStyles,
					uiControl: {
						type: 'ColorPicker',
						label: StringResources.get('propTabSelectedBarColor'),
						ariaLabel: StringResources.get('propTabSelectedBarColor'),
						paletteType: 'DashboardColorSet',
						open: false,
						showHexValue: true,
						addButton: true,
						onChange: (propertyName, propertyValueInfo = {}) => {
							this._getCanvas().setPropertyValue(propertyName, propertyValueInfo.name);
						}
					}
				}
			}, {
				id: 'tabBackgroundColor',
				supportsUndoRedo: true,
				setPropertyValue: (value, transactionToken) => {
					this._changeTabColor('tabBackgroundColor', value, transactionToken);
				},
				editor: {
					sectionId: sectionMasterTabStyles,
					uiControl: {
						type: 'ColorPicker',
						label: StringResources.get('propTabBackgroundColor'),
						ariaLabel: StringResources.get('propTabBackgroundColor'),
						paletteType: 'DashboardColorSet',
						open: false,
						showHexValue: true,
						addButton: true,
						onChange: (propertyName, propertyValueInfo = {}) => {
							this._getCanvas().setPropertyValue(propertyName, propertyValueInfo.name);
						}
					}
				}
			}, {
				id: 'tabRestoreDefaults',
				editor: {
					sectionId: sectionMasterTabStyles,
					uiControl: {
						type: 'SingleLineLinks',
						items: [{
							align: 'left',
							items: [{
								type: 'icon',
								class: 'iconTextLink',
								svgIcon: this._features['Dashboard.Icons'].getIcon('restore').id,
								iconTooltip: StringResources.get('propTabRestoreDefaults'),
								clickCallback: restoreMasterStyling
							}]
						}, {
							align: 'right',
							items: [{
								type: 'text',
								class: 'iconTextLink',
								value: StringResources.get('propTabRestoreDefaults'),
								ariaLabel: StringResources.get('propTabRestoreDefaults'),
								clickCallback: restoreMasterStyling
							}]
						}]
					}
				}
			}, {
				id: 'allowHidingMultipleTabs',
				getPropertyValue: () => this._allowHidingMultipleTabs,
				setPropertyValue: () => { throw new Error('not supported'); }
			}];
			if (this.isPropertiesV2Enabled) {
				// Modify the property 'RestoreDefault' for master style links to align with Carbon design:
				// left: transparent icon (display nothing instead of the text 'Restore defaults'); right: icon restore and justifyContent='flex-end'
				const restoreProperty = propertyList.find(property => property.id === 'tabRestoreDefaults');
				restoreProperty.editor.uiControl.justifyContent = 'flex-end';
				const left = restoreProperty.editor.uiControl.items[0].items[0];
				left.type = 'icon';
				left.svgIcon = null;
				delete left.iconTooltip;
				delete left.class;

				const right = restoreProperty.editor.uiControl.items[1].items[0];
				right.type = 'icon';
				right.svgIcon = this._features['Dashboard.Icons'].getIcon('restore').id;
				right.clickCallback = restoreMasterStyling;
				right.iconTooltip = StringResources.get('propTabRestoreDefaults');
				delete right.ariaLabel; // icon doesn't have these options
				delete right.value;

			}
			return propertyList.concat(this._getIndividualTabStylesLinks(sectionIndividualTabStylesId));
		}

		_changeTabColor(propertyName, propertyValue) {
			// get layout and update CSS
			const payload = {};
			// Treat transparent as reset to default
			if(propertyValue) {
				payload[propertyName] = propertyValue == 'transparent' ? null : this._dashboard.getFeature('Colors').getColorClassName(propertyValue);
			} else {
				payload[propertyName] = null;
			}

			this._dashboard.getFeature('Colors').prepareForColorModelChange(payload, propertyName);

			const model = this.getLayoutModel();
			model.set(payload, {
				sender: this.senderId,
				payloadData: { skipUndoRedo: true }
			});
		}

		_getPropertyValueFromModel(propName) {
			const model = this.getLayoutModel();
			// In case of livewidget previews, we have a content but it is not attached to the layout, which causes the model to be null.
			return model && model.get(propName);
		}

		_setPropertyValueInModel(propName, propValue) {
			const payload = {};
			const model = this.getLayoutModel();
			payload[propName] = propValue;
			model.set(payload, {
				payloadData: { skipUndoRedo: true }
			});
		}
	}

	return TabProperties;
});
